const PACKAGING_FACTORS = {
	Cardboard: { virgin: 1.20, recycled: 1.10 },
	PET: { virgin: 3.86, recycled: 2.21 },
	HDPE: { virgin: 3.10, recycled: 1.77 },
	LDPE: { virgin: 2.97, recycled: 1.10 },
	PP: { virgin: 2.58, recycled: 1.31 },
	"PVC/Other": { virgin: 2.94, recycled: 1.85 },
	Glass: { virgin: 1.40, recycled: 0.82 },
	Aluminum: { virgin: 9.12, recycled: 1.00 },
}

const PACKAGING_EOL_RECYCLE = 0.0047
const PACKAGING_EOL_INCINERATION = 0.0047
const PACKAGING_EOL_LANDFILL = {
	Cardboard: 1.16,
	PET: 0.0090,
	HDPE: 0.0090,
	LDPE: 0.0090,
	PP: 0.0090,
	"PVC/Other": 0.0090,
	Glass: 0.0090,
	Aluminum: 0.0090,
}

function computePackagingRowWeightKg(row, weightEntryMethod) {
	if (weightEntryMethod === "Unit weight × quantity") {
		const unitWeightKg = parseFloat(row.unitWeightKg ?? row.unitWeightG) || 0
		const quantity = parseFloat(row.quantity) || 0
		if (row.unitWeightG != null && row.unitWeightG !== "" && (row.unitWeightKg == null || row.unitWeightKg === "")) {
			return (parseFloat(row.unitWeightG) || 0) * quantity / 1000
		}
		return unitWeightKg * quantity
	}
	return parseFloat(row.totalWeightKg) || 0
}

function resolvePackagingEndOfLife(row, defaultEndOfLife) {
	if (row.endOfLife) {
		return row.endOfLife
	}
	if (defaultEndOfLife === "Mostly landfilled") {
		return "Landfill"
	}
	if (defaultEndOfLife === "Mostly incinerated") {
		return "Incineration (with energy recovery)"
	}
	if (defaultEndOfLife === "Mixed (50/50)") {
		return "Mixed (50/50)"
	}
	return defaultEndOfLife || "Landfill"
}

function getPackagingDisposalFactor(material, endOfLife) {
	if (endOfLife === "Landfill") {
		return PACKAGING_EOL_LANDFILL[material] ?? 0.0090
	}
	return PACKAGING_EOL_INCINERATION
}

export function calculatePackagingRowEmission(row, packaging) {
	const factors = PACKAGING_FACTORS[row.material]
	if (!factors) {
		return { totalWeightKg: 0, co2: 0 }
	}

	const totalWeightKg = computePackagingRowWeightKg(row, packaging.weightEntryMethod)
	if (totalWeightKg <= 0) {
		return { totalWeightKg: 0, co2: 0 }
	}

	const recycledContentRate = Math.min(100, Math.max(0, parseFloat(row.recycledContentPct) || 0)) / 100
	const recycledAfterUseRate = Math.min(100, Math.max(0, parseFloat(row.recycledAfterUsePct) || 0)) / 100
	const virginWeight = totalWeightKg * (1 - recycledContentRate)
	const recycledContentWeight = totalWeightKg * recycledContentRate
	const productionEmission =
		(virginWeight * factors.virgin) +
		(recycledContentWeight * factors.recycled)

	const wEolRecycled = totalWeightKg * recycledAfterUseRate
	const wEolDisposed = totalWeightKg * (1 - recycledAfterUseRate)
	const endOfLife = resolvePackagingEndOfLife(row, packaging.defaultEndOfLife)

	let endOfLifeEmission = wEolRecycled * PACKAGING_EOL_RECYCLE
	if (endOfLife === "Mixed (50/50)") {
		const landfillFactor = PACKAGING_EOL_LANDFILL[row.material] ?? 0.0090
		endOfLifeEmission +=
			(wEolDisposed * 0.5 * landfillFactor) +
			(wEolDisposed * 0.5 * PACKAGING_EOL_INCINERATION)
	} else {
		endOfLifeEmission += wEolDisposed * getPackagingDisposalFactor(row.material, endOfLife)
	}

	return {
		totalWeightKg,
		co2: productionEmission + endOfLifeEmission,
	}
}

export function calculatePackagingTotals(packaging) {
	if (!packaging?.materials?.length) {
		return { totalWeightKg: 0, totalCo2: 0 }
	}
	return packaging.materials.reduce((totals, row) => {
		const result = calculatePackagingRowEmission(row, packaging)
		totals.totalWeightKg += result.totalWeightKg
		totals.totalCo2 += result.co2
		return totals
	}, { totalWeightKg: 0, totalCo2: 0 })
}

export function getPackagingRowWeightKg(row, weightEntryMethod) {
	return computePackagingRowWeightKg(row, weightEntryMethod)
}

export function calculatePackagingEmission(packaging) {
	return calculatePackagingTotals(packaging).totalCo2
}
